import { useMemo } from "react"
import {
  Block,
  ListItem,
  ListItemLabel,
  ParagraphSmall,
  ParagraphXSmall,
  Tooltip,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { DateTime } from "luxon"
import { useLocalStorage } from "usehooks-ts"

import {
  DepositWithWithdrawalStatus,
  selectIsBitcoinWallet,
  useDappSelector,
} from "#/redux-state"
import {
  DATABASE_DECIMALS_COUNT,
  ENABLE_LOCK_TIME_REMOVAL,
  LOCAL_STORAGE_QA_MODE,
} from "#/shared/constants"
import { bigIntToFloat, getTokenIcon, isBridgeableToken } from "#/shared/utils"

import OverviewTokenUsdValue from "./OverviewTokenUsdValue"
import OverviewTokenValue from "./OverviewTokenValue"
import OverviewUnbridgedAssetsItemActions from "./OverviewUnbridgedAssetsItemActions"

type OverviewUnbridgedAssetsItemProps = {
  deposit: DepositWithWithdrawalStatus | null
  isLoading?: boolean
}

function DepositItem({ deposit, isLoading }: OverviewUnbridgedAssetsItemProps) {
  const { token, amount, migration, locks } = deposit ?? {
    token: "BTC",
    amount: "0",
    locks: [],
  }

  const [, theme] = useStyletron()

  const isBitcoinWallet = useDappSelector(selectIsBitcoinWallet)

  const currentToken = useMemo(
    () => (migration ? migration.toToken : token),
    [migration, token],
  )

  const isDepositBridgeable = useMemo(
    () => isBridgeableToken(currentToken) && !deposit?.withdrawn,
    [currentToken, deposit?.withdrawn],
  )

  const isDepositLocked = useMemo(() => {
    if (ENABLE_LOCK_TIME_REMOVAL || !locks.length) return false

    const sortedLocks = [...locks].sort(
      (a, b) =>
        new Date(a.lockStart).getTime() - new Date(b.lockStart).getTime(),
    )

    return (
      new Date(sortedLocks[sortedLocks.length - 1].lockEnd).getTime() >
      Date.now()
    )
  }, [locks])

  return (
    <ListItem
      state={isLoading ? "loading" : "enabled"}
      artwork={() => (
        <Block
          as="img"
          src={getTokenIcon(currentToken)}
          width={theme.sizing.scale900}
          height={theme.sizing.scale900}
        />
      )}
      endEnhancer={() =>
        deposit && (
          <OverviewUnbridgedAssetsItemActions
            deposit={deposit}
            isDepositLocked={isDepositLocked}
            isDepositBridgeable={isDepositBridgeable}
          />
        )
      }
      overrides={{
        Content: { style: () => ({ border: "none" }) },
        Root: {
          style: () => ({
            borderBottom: `1px solid ${theme.colors.borderOpaque}`,
          }),
        },
      }}
    >
      <ListItemLabel
        description={
          <>
            <ParagraphSmall
              color={theme.colors.contentSecondary}
              as="span"
              display="block"
            >
              <OverviewTokenUsdValue
                amount={BigInt(amount)}
                token={token}
                decimals={DATABASE_DECIMALS_COUNT}
              />
            </ParagraphSmall>
            <ParagraphSmall
              color={theme.colors.contentSecondary}
              as="span"
              display="block"
            >
              {isBitcoinWallet ? "Bitcoin" : "Ethereum"}
            </ParagraphSmall>
            {isDepositLocked && (
              <ParagraphSmall
                color={theme.colors.contentSecondary}
                as="span"
                display="block"
              >
                Unlocks on{" "}
                {DateTime.fromJSDate(
                  new Date(locks[locks.length - 1].lockEnd),
                ).toFormat("LLL dd yyyy")}
              </ParagraphSmall>
            )}
          </>
        }
      >
        <OverviewTokenValue
          amount={BigInt(amount)}
          token={currentToken}
          decimals={DATABASE_DECIMALS_COUNT}
        />
      </ListItemLabel>
    </ListItem>
  )
}

export default function OverviewUnbridgedAssetsItem({
  deposit,
  isLoading,
}: OverviewUnbridgedAssetsItemProps) {
  const [, theme] = useStyletron()

  const [isQaModeEnabled] = useLocalStorage<boolean>(
    LOCAL_STORAGE_QA_MODE,
    false,
  )

  if (isQaModeEnabled && deposit) {
    return (
      <Tooltip
        backgroundColor={theme.colors.blue100}
        content={
          <Block display="flex" flexDirection="column" alignItems="center">
            <ParagraphXSmall marginBottom={theme.sizing.scale100}>
              This tooltip should not be visible in production
            </ParagraphXSmall>
            <ParagraphSmall>UUID: {deposit.id}</ParagraphSmall>
            <ParagraphSmall>
              Amount: {bigIntToFloat(BigInt(deposit.amount), 18, 18)}
            </ParagraphSmall>
            <ParagraphSmall>Deposit ID: {deposit.depositId}</ParagraphSmall>
            <ParagraphSmall>
              Lock end:{" "}
              {deposit.locks[deposit.locks.length - 1].lockEnd.toString()}
            </ParagraphSmall>
            {deposit.migration && (
              <ParagraphSmall>Migrated from WBTC: true</ParagraphSmall>
            )}
          </Block>
        }
      >
        <Block>
          <DepositItem deposit={deposit} isLoading={isLoading} />
        </Block>
      </Tooltip>
    )
  }

  return <DepositItem deposit={deposit} isLoading={isLoading} />
}
